// Calendar (layer 3): a month grid with the agenda below, an add/edit form, weekly repeats with
// skipped dates, and the account's private subscription link. With `workspaceId` it shows and adds
// only that workspace's events (the workspace page's "Schedule").
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api } from '../api';
import { T, ex, t } from '../tenant';

export interface Occurrence {
  eventId: string;
  date: string;
  time: string | null;
  durationMin: number | null;
  title: string;
  kind: 'deadline' | 'session' | 'event';
  workspaceId: string | null;
  workspaceTitle: string | null;
  repeat: 'none' | 'weekly';
}
interface EventDef {
  id: string;
  workspace_id: string | null;
  title: string;
  kind: Occurrence['kind'];
  start_date: string;
  start_time: string | null;
  duration_min: number | null;
  repeat: 'none' | 'weekly';
  repeat_until: string | null;
  notes: string | null;
}
interface CalendarData {
  today: string;
  timezone: string;
  events: EventDef[];
  occurrences: Occurrence[];
}

const DAY = 86_400_000;
const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);
const monthStart = (d: string) => `${d.slice(0, 7)}-01`;
const addMonths = (d: string, n: number) => {
  const x = new Date(`${monthStart(d)}T00:00:00Z`);
  x.setUTCMonth(x.getUTCMonth() + n);
  return x.toISOString().slice(0, 10);
};
/** Monday-first weekday index (0 = Monday). */
const weekday = (d: string) => (new Date(`${d}T00:00:00Z`).getUTCDay() + 6) % 7;
export const longDate = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const monthLabel = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
const KIND_LABEL: Record<Occurrence['kind'], string> = { deadline: 'Deadline', session: 'Session', event: 'Event' };

export function Calendar({ workspaceId, workspaces, canEdit, onStartSession }: { workspaceId?: string; workspaces?: { id: string; title: string }[]; canEdit: boolean; onStartSession?: (o: Occurrence) => void }) {
  const [month, setMonth] = useState<string | null>(null);
  const [data, setData] = useState<CalendarData | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<EventDef | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const compact = !!workspaceId;

  // The grid shows whole weeks around the month; the agenda runs 60 days from today.
  const range = useMemo(() => {
    if (!month) return null;
    const first = monthStart(month);
    const gridFrom = addDays(first, -weekday(first));
    return { gridFrom, from: gridFrom, to: addDays(gridFrom, 41 + 60) };
  }, [month]);

  const load = async () => {
    const today = data?.today;
    const m = month ?? today;
    const from = range?.from ?? addDays(new Date().toISOString().slice(0, 10), -40);
    const to = range?.to ?? addDays(from, 140);
    try {
      const r = await api<CalendarData>(`/api/calendar?from=${from}&to=${to}${workspaceId ? `&workspace=${workspaceId}` : ''}`);
      setData(r);
      if (!m) setMonth(r.today);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.from, workspaceId]);

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      setEditing(null);
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (!data || !month || !range) return error ? <div className="error">{error}</div> : <div className="spinner" />;
  const byDate = new Map<string, Occurrence[]>();
  for (const o of data.occurrences) byDate.set(o.date, [...(byDate.get(o.date) ?? []), o]);
  const agendaFrom = selected ?? data.today;
  const agenda = selected ? (byDate.get(selected) ?? []) : data.occurrences.filter((o) => o.date >= agendaFrom && o.date <= addDays(data.today, 60));
  const defOf = (o: Occurrence) => data.events.find((e) => e.id === o.eventId);

  return (
    <div className="list" data-testid="calendar">
      {error && <div className="error">{error}</div>}
      {!compact && (
        <>
          <div className="row">
            <button className="btn sm" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month">
              ‹
            </button>
            <strong style={{ minWidth: 150, textAlign: 'center' }} data-testid="month-label">
              {monthLabel(monthStart(month))}
            </strong>
            <button className="btn sm" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month">
              ›
            </button>
            <button className="btn ghost sm" onClick={() => (setMonth(data.today), setSelected(null))}>
              Today
            </button>
            <span className="spacer" />
            <FeedLink />
          </div>
          <div className="cal-grid" role="grid" aria-label={monthLabel(monthStart(month))}>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div key={d} className="cal-head">
                {d}
              </div>
            ))}
            {Array.from({ length: 42 }, (_, i) => addDays(range.gridFrom, i)).map((d) => {
              const items = byDate.get(d) ?? [];
              const out = d.slice(0, 7) !== month.slice(0, 7);
              return (
                <button
                  key={d}
                  className={`cal-day${out ? ' out' : ''}${d === data.today ? ' today' : ''}${d === selected ? ' sel' : ''}`}
                  onClick={() => setSelected(d === selected ? null : d)}
                  data-testid="cal-day"
                  data-date={d}
                >
                  <span className="cal-num">{Number(d.slice(8))}</span>
                  {items.slice(0, 3).map((o, j) => (
                    <span key={j} className={`cal-chip ${o.kind}`}>
                      {o.time ? `${o.time} ` : ''}
                      {o.title}
                    </span>
                  ))}
                  {items.length > 3 && <span className="muted small">+{items.length - 3} more</span>}
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="row">
        <h2 className="h2" style={{ flex: 1, margin: 0 }}>
          {selected ? longDate(selected) : compact ? 'Schedule' : 'Coming up'}
        </h2>
        {selected && (
          <button className="btn ghost sm" onClick={() => setSelected(null)}>
            Show coming up
          </button>
        )}
        {canEdit && (
          <button className="btn primary sm" onClick={() => setEditing('new')} data-testid="add-event">
            Add
          </button>
        )}
      </div>
      {editing && (
        <EventForm
          initial={editing === 'new' ? null : editing}
          date={selected ?? data.today}
          workspaceId={workspaceId}
          workspaces={workspaces}
          onCancel={() => setEditing(null)}
          onSave={(body) => act(() => (editing === 'new' ? api('/api/calendar', { body }) : api(`/api/calendar/${editing.id}`, { method: 'PATCH', body })))}
        />
      )}
      {agenda.length === 0 && <div className="muted small">{selected ? 'Nothing on this day.' : 'Nothing in the next 60 days.'}</div>}
      {agenda.map((o) => (
        <div key={`${o.eventId}-${o.date}`} className="list-item" data-testid="agenda-item">
          <span className={`cal-dot ${o.kind}`} aria-hidden />
          <div style={{ flex: 1, minWidth: 160 }}>
            <div>
              <strong>{o.title}</strong> <span className="tag">{KIND_LABEL[o.kind]}</span>
              {o.repeat === 'weekly' && <span className="muted small"> · weekly</span>}
            </div>
            <div className="muted small">
              {longDate(o.date)}
              {o.time ? ` · ${o.time}${o.durationMin ? ` (${o.durationMin} min)` : ''}` : ''}
              {!compact && o.workspaceTitle ? ` · ${o.workspaceTitle}` : ''}
            </div>
          </div>
          {onStartSession && o.kind === 'session' && o.workspaceId && o.date === data.today && (
            <button className="btn primary sm" onClick={() => onStartSession(o)} data-testid="start-from-calendar">
              Start {t.meeting}
            </button>
          )}
          {canEdit && defOf(o) && (
            <>
              <button className="btn ghost sm" onClick={() => setEditing(defOf(o)!)}>
                Edit
              </button>
              {o.repeat === 'weekly' && (
                <button className="btn ghost sm" onClick={() => act(() => api(`/api/calendar/${o.eventId}/skip`, { body: { date: o.date } }))} title="Cancel this week only">
                  Skip this date
                </button>
              )}
              <button className="btn ghost sm" onClick={() => act(() => api(`/api/calendar/${o.eventId}`, { method: 'DELETE' }))}>
                Delete{o.repeat === 'weekly' ? ' all' : ''}
              </button>
            </>
          )}
        </div>
      ))}
      {compact && <div className="muted small">All {t.workspaces}' dates are on the Calendar tab, which can also go into your phone's calendar.</div>}
    </div>
  );
}

function EventForm({
  initial,
  date,
  workspaceId,
  workspaces,
  onSave,
  onCancel,
}: {
  initial: EventDef | null;
  date: string;
  workspaceId?: string;
  workspaces?: { id: string; title: string }[];
  onSave: (body: Record<string, unknown>) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<Occurrence['kind']>(initial?.kind ?? (workspaceId ? 'session' : 'deadline'));
  const [repeat, setRepeat] = useState(initial?.repeat === 'weekly');
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void onSave({
      title: f.get('title'),
      kind,
      date: f.get('date'),
      time: f.get('time') || null,
      durationMin: kind === 'session' && f.get('time') ? Number(f.get('duration') || 60) : null,
      repeat: repeat ? 'weekly' : 'none',
      until: repeat ? f.get('until') || null : null,
      notes: f.get('notes') || null,
      workspaceId: workspaceId ?? (f.get('workspaceId') || null),
    });
  }
  return (
    <form className="list event-form" onSubmit={submit} data-testid="event-form">
      <div className="row">
        {(['deadline', 'session', 'event'] as const).map((k) => (
          <label key={k} className="row small">
            <input type="radio" name="kind" checked={kind === k} onChange={() => setKind(k)} /> {KIND_LABEL[k]}
          </label>
        ))}
      </div>
      <input name="title" className="field" placeholder={kind === 'deadline' ? `e.g. ${ex.event}` : `e.g. ${ex.meeting}`} defaultValue={initial?.title} maxLength={120} required data-testid="event-title" />
      <div className="row">
        <label className="small">
          Date <input name="date" type="date" className="field" defaultValue={initial?.start_date ?? date} required data-testid="event-date" />
        </label>
        <label className="small">
          Time {kind === 'deadline' ? '(optional)' : ''} <input name="time" type="time" className="field" defaultValue={initial?.start_time ?? (kind === 'session' ? '15:00' : '')} data-testid="event-time" />
        </label>
        {kind === 'session' && (
          <label className="small">
            Minutes <input name="duration" type="number" min={5} max={600} step={5} className="field" style={{ width: 90 }} defaultValue={initial?.duration_min ?? 60} />
          </label>
        )}
      </div>
      <label className="row small">
        <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} data-testid="event-weekly" /> Every week
      </label>
      {repeat && (
        <label className="small">
          Until (leave empty for no end) <input name="until" type="date" className="field" defaultValue={initial?.repeat_until ?? ''} />
        </label>
      )}
      {!workspaceId && workspaces && (
        <label className="small">
          {T.workspace}
          <select name="workspaceId" className="field" defaultValue={initial?.workspace_id ?? ''}>
            <option value="">None (general)</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.title}
              </option>
            ))}
          </select>
        </label>
      )}
      <textarea name="notes" className="field" rows={2} placeholder="Notes (optional)" defaultValue={initial?.notes ?? ''} maxLength={1000} />
      <div className="row">
        <button className="btn primary sm" data-testid="save-event">
          Save
        </button>
        <button type="button" className="btn ghost sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** The account's private calendar URL, for Google/Apple/Outlook "subscribe from URL". */
function FeedLink() {
  const [path, setPath] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (open && !path) api<{ path: string }>('/api/calendar/feed').then((r) => setPath(r.path), () => {});
  }, [open, path]);
  const url = path ? `${location.origin}${path}` : '';
  return (
    <div className="feed">
      <button className="btn sm" onClick={() => setOpen(!open)} data-testid="feed-button">
        Add to my phone's calendar
      </button>
      {open && path && (
        <div className="feed-pop list" data-testid="feed">
          <div className="small">
            Subscribe to this private address in Google Calendar (Other calendars → From URL), Apple Calendar (New Calendar Subscription) or Outlook (Add calendar → From internet). Updates appear within a few hours.
          </div>
          <div className="linkbox">
            <input className="field" readOnly value={url} onFocus={(e) => e.currentTarget.select()} aria-label="Calendar address" />
            <button
              className="btn primary sm"
              onClick={async () => {
                await navigator.clipboard?.writeText(url).catch(() => {});
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => api<{ path: string }>('/api/calendar/feed', { body: {} }).then((r) => setPath(r.path))}>
            Make a new address (the old one stops working)
          </button>
        </div>
      )}
    </div>
  );
}
